import { zodResolver } from '@hookform/resolvers/zod';
import { Info, Play } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { useCampaignAnalysisPreflight } from '@/features/campaign/hooks/query';
import {
  CAMPAIGN_ANALYSIS_DIMENSION,
  CAMPAIGN_ANALYSIS_TARGET_METRIC,
} from '@/entities/campaign/generated/campaign-constants';
import { createCampaignAnalysisRequestSchema } from '@/entities/campaign/generated/campaign-schema';
import type {
  CampaignAnalysisDimension,
  CreateCampaignAnalysisRequest,
} from '@/entities/campaign/generated/campaign-type';
import { FieldInfoIcon, FieldSection } from '@/features/campaign/campaign-editor/primitives';
import { getCampaignAnalysisExclusionReasons } from '@/features/campaign/helpers/campaign-analysis-eligibility-copy';
import { Alert, AlertDescription, AlertTitle } from '@/shared/components/shadcn/alert';
import { Button } from '@/shared/components/shadcn/button';
import { Checkbox } from '@/shared/components/shadcn/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/shadcn/dialog';
import { Form, FormField } from '@/shared/components/shadcn/form';
import { RadioGroup, RadioGroupItem } from '@/shared/components/shadcn/radio-group';
import { Skeleton } from '@/shared/components/shadcn/skeleton';
import { TooltipFlat } from '@/shared/components/shadcn/tooltip/tooltip-flat';
import { cn } from '@/shared/utils';
import {
  CAMPAIGN_ANALYSIS_REPORT_LIMIT_REACHED,
  isCampaignAnalysisReportUsageAtLimit,
  type CampaignAnalysisReportUsage,
} from '../helpers/analysis-report-usage';

type Props = {
  campaignId: string;
  isOpen: boolean;
  onClose: () => void;
  reportUsage?: CampaignAnalysisReportUsage;
  onSubmit: (request: CreateCampaignAnalysisRequest) => Promise<void>;
};

type CampaignAnalysisCreateFormValues = CreateCampaignAnalysisRequest;

const METRIC_OPTIONS = [
  {
    goal: 'Reach',
    hint: 'Find posts that generated stronger view performance.',
    title: 'View Score',
    value: CAMPAIGN_ANALYSIS_TARGET_METRIC.VIEW_SCORE,
  },
  {
    goal: 'Conversion',
    hint: 'Find posts that generated stronger likes, comments, and shares.',
    title: 'Engagement Score',
    value: CAMPAIGN_ANALYSIS_TARGET_METRIC.ENGAGEMENT_SCORE,
  },
] as const satisfies ReadonlyArray<{
  goal: string;
  hint: string;
  title: string;
  value: CreateCampaignAnalysisRequest['target_metric'];
}>;

const DIMENSION_OPTIONS = [
  {
    description: 'What the video does in the first 3 seconds to stop the scroll',
    title: 'Hook',
    value: CAMPAIGN_ANALYSIS_DIMENSION.HOOK,
  },
  {
    description: 'The main selling point the video highlights',
    title: 'Key message',
    value: CAMPAIGN_ANALYSIS_DIMENSION.KEY_MESSAGE,
  },
  {
    description: 'How the claim is backed up — before-and-afters, data, demos, etc.',
    title: 'Proof point',
    value: CAMPAIGN_ANALYSIS_DIMENSION.PROOF_POINT,
  },
  {
    description: "The narrative structure — how the video's message unfolds from start to finish",
    title: 'Structure',
    value: CAMPAIGN_ANALYSIS_DIMENSION.STRUCTURE,
  },
  {
    description: 'What viewers are asked to do next — comment, save, shop, etc.',
    title: 'CTA',
    value: CAMPAIGN_ANALYSIS_DIMENSION.CTA,
  },
  {
    description: 'The type of content viewers see it as — vlog, review, tutorial, etc.',
    title: 'Content format',
    value: CAMPAIGN_ANALYSIS_DIMENSION.CONTENT_FORMAT,
  },
  {
    description: 'How the creator shows up on camera — face, hands only, product only, etc.',
    title: 'Shot type',
    value: CAMPAIGN_ANALYSIS_DIMENSION.SHOT_TYPE,
  },
  {
    description: 'The role the creator speaks from — expert, everyday user, etc.',
    title: 'POV',
    value: CAMPAIGN_ANALYSIS_DIMENSION.POV,
  },
  {
    description: 'Why the creator says they use the product',
    title: 'Occasion',
    value: CAMPAIGN_ANALYSIS_DIMENSION.OCCASION,
  },
  {
    description: 'What carries the sound — voiceover, music, ambient noise, etc.',
    title: 'Audio',
    value: CAMPAIGN_ANALYSIS_DIMENSION.AUDIO,
  },
  {
    description: 'The overall vibe and emotional energy of the video',
    title: 'Tone and mood',
    value: CAMPAIGN_ANALYSIS_DIMENSION.TONE_AND_MOOD,
  },
  {
    description: 'How polished the video feels, from casual phone footage to studio-grade',
    title: 'Production value',
    value: CAMPAIGN_ANALYSIS_DIMENSION.PRODUCTION_VALUE,
  },
] as const satisfies ReadonlyArray<{
  description: string;
  title: string;
  value: CampaignAnalysisDimension;
}>;

const dimensionValues = DIMENSION_OPTIONS.map((option) => option.value);

function getDimensionSelectionRange() {
  const validCounts = Array.from(
    { length: dimensionValues.length + 1 },
    (_, count) => count,
  ).filter(
    (count) =>
      createCampaignAnalysisRequestSchema.shape.dimensions.safeParse(
        dimensionValues.slice(0, count),
      ).success,
  );
  const minimum = validCounts.at(0);
  const maximum = validCounts.at(-1);

  if (minimum === undefined || maximum === undefined) {
    throw new Error('Campaign analysis dimensions must allow at least one selection count.');
  }

  return { maximum, minimum };
}

export const { maximum: MAXIMUM_DIMENSIONS, minimum: MINIMUM_DIMENSIONS } =
  getDimensionSelectionRange();

const DEFAULT_FORM_VALUES: CampaignAnalysisCreateFormValues = {
  dimensions: [],
  target_metric: CAMPAIGN_ANALYSIS_TARGET_METRIC.VIEW_SCORE,
};

export function CampaignAnalysisCreateDialog({
  campaignId,
  isOpen,
  onClose,
  reportUsage,
  onSubmit,
}: Props) {
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const preflight = useCampaignAnalysisPreflight(campaignId, { enabled: isOpen });
  const form = useForm<CampaignAnalysisCreateFormValues>({
    defaultValues: DEFAULT_FORM_VALUES,
    resolver: zodResolver(createCampaignAnalysisRequestSchema),
  });
  const selectedDimensions = useWatch({
    control: form.control,
    name: 'dimensions',
  });
  const isSubmitting = form.formState.isSubmitting;
  const trackedPostsCount = preflight.data?.total ?? 0;
  const excludedPostsCount = preflight.data?.exclude ?? 0;
  const eligiblePostsCount = preflight.data?.analyzable ?? 0;
  const minimumRequiredPostsCount = preflight.data?.minimum_required ?? 0;
  const canRunAnalysis = preflight.data?.can_run ?? false;
  const hasReachedReportLimit = isCampaignAnalysisReportUsageAtLimit(reportUsage);
  const hasSelectedDimensions = selectedDimensions.length >= MINIMUM_DIMENSIONS;
  const canAnalyze =
    canRunAnalysis && hasSelectedDimensions && !hasReachedReportLimit && !isSubmitting;
  const exclusionReasons = getCampaignAnalysisExclusionReasons();
  const dimensionInputStatus = hasSelectedDimensions
    ? `${selectedDimensions.length}/${MAXIMUM_DIMENSIONS} selected`
    : `Select ${MINIMUM_DIMENSIONS} to ${MAXIMUM_DIMENSIONS}`;

  const closeDialog = () => {
    if (isSubmitting) return;

    setSubmissionError(null);
    form.reset(DEFAULT_FORM_VALUES);
    onClose();
  };

  const submitAnalysis = async (values: CampaignAnalysisCreateFormValues) => {
    setSubmissionError(null);

    try {
      await onSubmit(buildCreateCampaignAnalysisRequest(values));
      form.reset(DEFAULT_FORM_VALUES);
      onClose();
    } catch {
      setSubmissionError('The analysis could not be started. Check your connection and try again.');
    }
  };

  return (
    <Dialog
      onOpenChange={(open) => {
        if (!open) closeDialog();
      }}
      open={isOpen}
    >
      <DialogContent className="max-h-[calc(100vh-4rem)] overflow-y-auto sm:max-w-190">
        <DialogHeader>
          <DialogTitle>Analyze campaign</DialogTitle>
          <DialogDescription>
            Choose how Syncly should evaluate this campaign before generating the report.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            className="flex flex-col gap-5"
            noValidate
            onSubmit={form.handleSubmit(submitAnalysis)}
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="min-w-0">
                <p className="text-muted-foreground text-sm font-medium">Posts to analyze</p>
                {preflight.isLoading ? (
                  <Skeleton className="mt-1 h-9 w-40" />
                ) : (
                  <p
                    className={cn(
                      'mt-1 text-3xl leading-9 font-semibold tracking-tight',
                      canRunAnalysis ? 'text-foreground' : 'text-destructive',
                    )}
                  >
                    {eligiblePostsCount}
                    <span className="ml-2 text-base font-medium tracking-normal">
                      / {minimumRequiredPostsCount} required
                    </span>
                  </p>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-2 sm:text-right">
                <AnalysisDataSummaryItem
                  isLoading={preflight.isLoading}
                  label="Tracked posts"
                  value={trackedPostsCount}
                />
                <AnalysisDataSummaryItem
                  info={<ExclusionReasonList reasons={exclusionReasons} />}
                  isLoading={preflight.isLoading}
                  label="Excluded"
                  value={excludedPostsCount}
                />
              </div>
            </div>

            <FormField
              control={form.control}
              name="target_metric"
              render={({ field }) => (
                <FieldSection title="Target metric">
                  <RadioGroup
                    className="gap-2"
                    disabled={isSubmitting}
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    {METRIC_OPTIONS.map((option) => {
                      const optionId = `campaign-analysis-metric-${option.value}`;
                      const isSelected = field.value === option.value;

                      return (
                        <label
                          className={cn(
                            'flex items-start rounded-xl border p-4 transition-colors',
                            isSelected
                              ? 'border-foreground bg-accent'
                              : 'border-border bg-card hover:border-foreground/20',
                          )}
                          htmlFor={optionId}
                          key={option.value}
                        >
                          <RadioGroupItem className="mt-0.5" id={optionId} value={option.value} />
                          <span className="ml-3 min-w-0 flex-1">
                            <span className="flex flex-wrap items-center gap-2">
                              <span className="text-card-foreground text-sm font-medium">
                                {option.title}
                              </span>
                              <span className="text-muted-foreground rounded-full border px-2 py-0.5 text-xs">
                                {option.goal}
                              </span>
                            </span>
                            <span className="text-muted-foreground mt-1 block text-sm leading-5">
                              {option.hint}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </RadioGroup>
                </FieldSection>
              )}
            />

            <FieldSection title="Analysis dimensions">
              <div className="mb-3 flex flex-col gap-2">
                <p className="text-muted-foreground flex-1 text-sm leading-5">
                  Select the dimensions you&apos;d like the report to focus on. Each one you select
                  is guaranteed at least one insight in the report. (The analysis isn&apos;t limited
                  to your selection — many other dimensions are explored automatically.)
                </p>
                <span
                  className={cn(
                    'text-sm font-semibold',
                    hasSelectedDimensions ? 'text-foreground' : 'text-destructive',
                  )}
                >
                  {dimensionInputStatus}
                </span>
              </div>

              <FormField
                control={form.control}
                name="dimensions"
                render={({ field }) => (
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {DIMENSION_OPTIONS.map((option) => {
                      const optionId = `campaign-analysis-dimension-${option.value}`;
                      const isSelected = field.value.includes(option.value);
                      const hasReachedSelectionLimit = field.value.length >= MAXIMUM_DIMENSIONS;

                      return (
                        <TooltipFlat
                          asChild
                          content={option.description}
                          contentClassName="max-w-64 text-sm leading-5"
                          key={option.value}
                        >
                          <label
                            className="flex cursor-pointer items-center gap-2 py-1.5"
                            htmlFor={optionId}
                          >
                            <Checkbox
                              checked={isSelected}
                              disabled={isSubmitting || (!isSelected && hasReachedSelectionLimit)}
                              id={optionId}
                              onCheckedChange={(checked) =>
                                field.onChange(
                                  toggleDimension(field.value, option.value, checked === true),
                                )
                              }
                            />
                            <span className="text-card-foreground min-w-0 truncate text-sm font-medium">
                              {option.title}
                            </span>
                          </label>
                        </TooltipFlat>
                      );
                    })}
                  </div>
                )}
              />
            </FieldSection>

            {preflight.data != null && !canRunAnalysis ? (
              <Alert variant="destructive">
                <AlertTitle>Not enough eligible posts</AlertTitle>
                <AlertDescription>
                  <p>
                    Campaign analysis needs at least {minimumRequiredPostsCount} eligible posts.
                  </p>
                  <ExclusionReasonList className="mt-2" reasons={exclusionReasons} />
                </AlertDescription>
              </Alert>
            ) : null}

            {submissionError ? (
              <Alert variant="destructive">
                <AlertDescription>{submissionError}</AlertDescription>
              </Alert>
            ) : null}

            {canRunAnalysis && hasSelectedDimensions ? (
              <div className="bg-muted/50 text-muted-foreground flex items-start gap-2 rounded-xl border p-3 text-sm leading-5">
                <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <p>
                  Analysis usually takes 30 minutes to 1 hour. You can leave this page and come back
                  later.
                </p>
              </div>
            ) : null}

            <DialogFooter>
              <Button disabled={isSubmitting} onClick={closeDialog} type="button" variant="outline">
                Cancel
              </Button>
              {hasReachedReportLimit ? (
                <TooltipFlat
                  asChild
                  content={CAMPAIGN_ANALYSIS_REPORT_LIMIT_REACHED}
                  contentClassName="max-w-72 text-sm leading-5"
                >
                  <span aria-label={CAMPAIGN_ANALYSIS_REPORT_LIMIT_REACHED} tabIndex={0}>
                    <Button disabled loading={isSubmitting} type="submit">
                      <Play aria-hidden="true" data-icon="inline-start" />
                      Start analysis
                    </Button>
                  </span>
                </TooltipFlat>
              ) : (
                <Button disabled={!canAnalyze} loading={isSubmitting} type="submit">
                  <Play aria-hidden="true" data-icon="inline-start" />
                  Start analysis
                </Button>
              )}
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function toggleDimension(
  currentDimensions: CampaignAnalysisDimension[],
  dimension: CampaignAnalysisDimension,
  checked: boolean,
) {
  if (checked) {
    if (currentDimensions.length >= MAXIMUM_DIMENSIONS) return currentDimensions;

    return currentDimensions.includes(dimension)
      ? currentDimensions
      : [...currentDimensions, dimension];
  }

  return currentDimensions.filter((currentDimension) => currentDimension !== dimension);
}

function buildCreateCampaignAnalysisRequest(
  values: CampaignAnalysisCreateFormValues,
): CreateCampaignAnalysisRequest {
  return createCampaignAnalysisRequestSchema.parse({
    dimensions: values.dimensions,
    target_metric: values.target_metric,
  });
}

function AnalysisDataSummaryItem({
  info,
  isLoading,
  label,
  value,
}: {
  info?: ReactNode;
  isLoading: boolean;
  label: string;
  value: number | string;
}) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 sm:justify-end">
        <p className="text-muted-foreground text-sm font-medium">{label}</p>
        {info ? <FieldInfoIcon ariaLabel={`${label} info`} content={info} /> : null}
      </div>
      {isLoading ? (
        <Skeleton className="mt-1 h-5 w-10 sm:ml-auto" />
      ) : (
        <p className="text-foreground mt-1 truncate text-sm font-semibold">{value}</p>
      )}
    </div>
  );
}

function ExclusionReasonList({
  className,
  reasons,
}: {
  className?: string;
  reasons: readonly string[];
}) {
  return (
    <ul className={cn('list-disc space-y-1 pl-4', className)}>
      {reasons.map((reason) => (
        <li key={reason}>{reason}</li>
      ))}
    </ul>
  );
}
